import { Fade, Paper, Typography } from '@mui/material';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { FilePond, registerPlugin } from 'react-filepond';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import EmbeddingDetails from 'pages/embedding/components/EmbeddingDetails';
import { useNotification } from 'hook/useNotification';
import { useStoreMultipleFilesMutation } from 'services/ragApi';
import { useGetKnowledgeBaseQuery } from 'services/knowledgeBaseApi';
import MDBox from 'components/MDBox';
import 'filepond/dist/filepond.min.css';
import './embedding.css';

registerPlugin(FilePondPluginFileValidateType);

export default function Embedding() {
  const { addNotification } = useNotification();
  const { formatMessage: msg } = useIntl();

  const [files, setFiles] = useState([]);
  const [dropzoneKey, setDropzoneKey] = useState(0);

  const [storeMultipleFiles] = useStoreMultipleFilesMutation();
  const { knowledgeBaseId } = useParams();

  const { data: knowledgeBase, isLoading: knowledgeBaseLoading } =
    useGetKnowledgeBaseQuery(knowledgeBaseId);

  const handleFormSubmit = async (data, resetFormCallback, stopLoading) => {
    if (files.length === 0) {
      addNotification({
        type: 'error',
        title: msg({ id: 'notification.error.title' }),
        message: msg({ id: 'file.upload.noFilesSelected' })
      });
      return;
    }

    // Extract actual File objects from FilePond file objects
    const fileObjects = files.map((fileItem) => fileItem.file);

    const requestData = {
      files: fileObjects,
      knowledgeBaseId: Number(knowledgeBaseId),
      maxChunkCount: data.maxChunkCount,
      metadata: data.metadata.reduce((acc, { key, value }) => {
        acc[key] = value;
        return acc;
      }, {})
    };

    try {
      await storeMultipleFiles(requestData).unwrap();
      addNotification({
        type: 'success',
        title: msg({ id: 'notification.success.title' }),
        message: msg({ id: 'file.embedding.success' }, { filesCount: files.length })
      });

      handleDetailsFormReset();
      resetFormCallback();
    } catch (error) {
      console.error('Error storing files:', error);
      addNotification({
        type: 'error',
        title: msg({ id: 'notification.error.title' }),
        message: msg({ id: 'file.upload.error' })
      });
    }
    stopLoading();
  };

  const handleDetailsFormReset = () => {
    setFiles([]);
    setDropzoneKey((prevKey) => prevKey + 1);
  };

  return (
    <Fade in timeout={(1000, 500)}>
      <MDBox
        flex={1}
        display="flex"
        flexDirection="column"
        sx={{ height: '100%' }}
        id="embeddingContainer"
      >
        <Paper elevation={2} sx={{ borderRadius: '10px', p: 2, mb: 1, overflow: 'hidden' }}>
          <Typography variant="h6" ml={2}>
            {knowledgeBaseLoading ? 'Chargement en cours' : knowledgeBase?.label}
          </Typography>
        </Paper>
        <Paper
          elevation={2}
          sx={{
            flex: 1,
            borderRadius: '10px',
            p: 2,
            mb: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <MDBox id="mainContainer" flex={1} display="flex" sx={{ height: '100%' }}>
            <MDBox
              id="uploadContainer"
              display="flex"
              flex={0.5}
              flexDirection="column"
              sx={{ height: '100%' }}
            >
              <MDBox id="drop" display="flex" flex={1}>
                <FilePond
                  key={dropzoneKey}
                  files={files}
                  onupdatefiles={setFiles}
                  allowMultiple
                  maxFiles={20}
                  name="files"
                  labelIdle={msg({ id: 'file.upload.description' })}
                  className="filepond-custom"
                  style={{ width: '100%', height: '100%' }}
                />
              </MDBox>
            </MDBox>

            <MDBox
              id="detailsContainer"
              display="flex"
              flex={0.5}
              pl={2}
              flexDirection="column"
              sx={{ height: '100%' }}
            >
              <EmbeddingDetails onSubmit={handleFormSubmit} onReset={handleDetailsFormReset} />
            </MDBox>
          </MDBox>
        </Paper>
      </MDBox>
    </Fade>
  );
}
